import React, { useState, useImperativeHandle, forwardRef } from 'react';

import { FullScreenLoading, BackrsFormTextField, BackrsUser } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ActivationCodeInput } from '@backrs_ui/grow-ui/src/components/ActivationCodeInput/ActivationCodeInput';
import { useRefreshEntitlements } from '@backrs_ui/grow-ui/src/hooks/useRefreshEntitlements';

import { Box, SxProps, Theme } from '@mui/material';
import { useForm, Controller, FieldValues } from 'react-hook-form';

import { useAgentContext } from './AccountSettings.agentContext';
import { FieldRow } from '../atoms/FieldRow';
import { SectionHeader } from '../atoms/SectionHeader';
import { SECTIONS_KEYS, SETTINGS_FIELDS } from '../config/settings.config';
import { useSettingsFormSubmit } from '../hooks/useSettingsOnSubmit';

const SETTINGS_STRINGS = appStrings.modules.accountSettings.settings;

// Helper function to convert MM/DD/YYYY to YYYY-MM-DD for HTML date input
const formatBirthdayForDateInput = (birthday: string): string => {
  if (!birthday) return '';

  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
    return birthday;
  }

  // Convert from MM/DD/YYYY to YYYY-MM-DD
  const parts = birthday.split('/');

  if (parts.length === 3) {
    const [month, day, year] = parts;

    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return birthday;
};

type AccountSettingsProps = Readonly<{
  user: BackrsUser;
  onFormChange: (isSubmitting: boolean, formSaveError: string | null) => void;
}>;

export interface AccountSettingsRef {
  submitForm: () => void;
  resetForm: () => void;
}

export const AccountSettings = forwardRef<AccountSettingsRef, AccountSettingsProps>(
  ({ user, onFormChange }, ref) => {
    const [formSaveError, setFormSaveError] = useState<string | null>(null);

    const defaultValues: FieldValues = {
      firstName: user?.first_name ?? '',
      lastName: user?.last_name ?? '',
      email: user?.email ?? '',
      phone: user?.phone ?? '',
      zipCode: user?.zip_code ?? '',
      birthday: user?.birthday ?? '',
      newPassword: '',
      confirmPassword: '',
    };

    const {
      control,
      handleSubmit,
      reset,
      setError,
      formState: { isSubmitting },
    } = useForm<FieldValues>({
      defaultValues,
      mode: 'onBlur',
      reValidateMode: 'onChange',
    });

    const submitHandler = useSettingsFormSubmit(user, setError, setFormSaveError);
    const onSubmit = handleSubmit(submitHandler);

    useAgentContext();

    const refreshEntitlements = useRefreshEntitlements();

    // Expose form methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        submitForm: onSubmit,
        resetForm: () => {
          reset({
            firstName: user.first_name,
            lastName: user.last_name,
            email: user.email,
            phone: user.phone,
            zipCode: user.zip_code,
            birthday: user.birthday,
            newPassword: '',
            confirmPassword: '',
          });
          setFormSaveError(null);
        },
      }),
      [onSubmit, reset, user],
    );

    // Notify parent of form state changes
    React.useEffect(() => {
      onFormChange(isSubmitting, formSaveError);
    }, [isSubmitting, formSaveError, onFormChange]);

    if (!user) {
      return <FullScreenLoading />;
    }

    return (
      <Box sx={containerSx}>
        {/* FORM */}
        <form onSubmit={onSubmit}>
          <Box sx={formSectionContainerSx}>
            {/* FIRST/LAST NAME */}
            <FieldRow label={SETTINGS_STRINGS.sections.name.title}>
              {SECTIONS_KEYS.NAME.map((key) => (
                <Controller
                  key={key}
                  name={key}
                  control={control}
                  rules={SETTINGS_FIELDS[key].rules}
                  render={({ field: controllerField, fieldState: { error } }) => (
                    <BackrsFormTextField
                      field={{ ...SETTINGS_FIELDS[key], label: null }}
                      groupedFields={{ name: SECTIONS_KEYS.NAME.map((k) => SETTINGS_FIELDS[k]) }}
                      index={0} // the index is only used for margin styling, which we don't want here
                      controllerField={controllerField}
                      error={error?.message}
                    />
                  )}
                />
              ))}
            </FieldRow>

            {/* OTHER FIELDS */}
            {SECTIONS_KEYS.OTHER.map((key, index) => (
              <Controller
                key={key}
                name={key}
                control={control}
                rules={SETTINGS_FIELDS[key].rules}
                render={({ field: controllerField, fieldState: { error } }) => {
                  const formattedValue =
                    key === 'birthday'
                      ? formatBirthdayForDateInput(controllerField.value ?? '')
                      : controllerField.value;

                  return (
                    <FieldRow
                      label={SETTINGS_FIELDS[key].label}
                      hideDivider={SETTINGS_FIELDS[key].hideDivider}
                    >
                      <BackrsFormTextField
                        field={{
                          ...SETTINGS_FIELDS[key],
                          label: null,
                        }}
                        groupedFields={{ [key]: [SETTINGS_FIELDS[key]] }}
                        index={index}
                        controllerField={{
                          ...controllerField,
                          value: formattedValue,
                        }}
                        error={error?.message}
                      />
                    </FieldRow>
                  );
                }}
              />
            ))}
          </Box>

          {/* PASSWORD/CONFIRM PASSWORD */}
          <SectionHeader
            title={SETTINGS_STRINGS.sections.password.title}
            description={SETTINGS_STRINGS.sections.password.description}
          />
          <Box sx={formSectionContainerSx}>
            {SECTIONS_KEYS.PASSWORD.map((key, index) => (
              <Controller
                key={key}
                name={key}
                control={control}
                rules={SETTINGS_FIELDS[key].rules}
                render={({ field: controllerField, fieldState: { error } }) => (
                  <FieldRow
                    label={SETTINGS_FIELDS[key].label}
                    hideDivider={SETTINGS_FIELDS[key].hideDivider}
                  >
                    <BackrsFormTextField
                      field={{ ...SETTINGS_FIELDS[key], label: null }}
                      groupedFields={{ [key]: [SETTINGS_FIELDS[key]] }}
                      index={index}
                      controllerField={controllerField}
                      error={error?.message}
                    />
                  </FieldRow>
                )}
              />
            ))}
          </Box>
        </form>

        {/* ACTIVATION CODE */}
        <SectionHeader
          title={SETTINGS_STRINGS.sections.activationCode.title}
          description={SETTINGS_STRINGS.sections.activationCode.description}
        />
        {/* Refreshes in place — a reload would discard unsaved edits to the form above. */}
        <ActivationCodeInput
          idPrefix="settings-activation-code"
          textAlign="left"
          onSuccess={refreshEntitlements}
        />
      </Box>
    );
  },
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: {
    xs: backrs.spacing['spacing-2xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
  margin: '0 auto',
  overflowY: 'auto',
  maxWidth: '1144px !important',
  width: '100%',

  mb: { xs: backrs.spacing['spacing-8xl'], sm: backrs.spacing['spacing-none'] },
});

const formSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
});
